import React, { useState, useEffect, useRef } from "react";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    Toast,
    UserSelectionMetricCopy,
    UserSelectionMetricDescription,
    UserSelectionMetricSummary,
    UserSelectionMetricTitle,
    UserSelectionMetricValue,
    UserViewerListPreview
} from "src/components";
import { AUDIENCE } from "../Advertising";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import pluralize from "pluralize";
import { customNames, capitalizeString } from "src/utils/helpers";

const getAdvertisingClicks = (token, ad) => {
    const controller = new AbortController();
    const isMountedRef = useRef(true);
    const [advertisingClicks, setAdvertisingClicks] = useState([]);
    const [isFetchingClicks, setIsFetchingClicks] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            setIsFetchingClicks(true);
            try {
                const config = {
                    signal: controller.signal,
                    headers: {
                        Authorization: "Bearer " + token
                    }
                };

                const response = await axios.get(`${API_URL}/advertising/${ad.uuid}/clicks`, config);
                
                if (isMountedRef.current) {
                    setAdvertisingClicks(response.data.clicks);
                }
            } catch {
                if (isMountedRef.current) {
                    Toast.error("Unable to fetch advertisement. Please try again later or contact support if the issue persists.");
                }
            } finally {
                if (isMountedRef.current) {
                    setIsFetchingClicks(false);
                }
            }
        };

        fetch();

        return () => {
            controller.abort();
            isMountedRef.current = false;
        };

    }, [token]);

    return { advertisingClicks, isFetchingClicks };
};

const AdvertisingClicksModal = ({ adData, showAdClicksModal, handleCloseAdClicksModal, user }) => {
    const { advertisingClicks, isFetchingClicks } = getAdvertisingClicks(user.token, adData);
    
    const userTypeDefault = () => {
        if (adData.displayToEmployees) {
            return AUDIENCE.EMPLOYEE;
        } else if (adData.displayToClients) {
            return AUDIENCE.CLIENT;
        } else {
            return AUDIENCE.EMPLOYEE; //default to employees
        }
    };

    const filterClicksByUserType = (userType) => {
        return userType === AUDIENCE.EMPLOYEE ? 
            advertisingClicks.employees : 
            advertisingClicks.clients;
    };

    const [userType, setUserType] = useState(userTypeDefault());
    const [viewersList, setViewersList] = useState(null);

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        setUserType(value);
    };

    useEffect(() => {
        const filteredList = filterClicksByUserType(userType);
        setViewersList(filteredList);
    }, [isFetchingClicks]);
    
    useEffect(() => {
        const filteredList = filterClicksByUserType(userType);
        setViewersList(filteredList);
    }, [userType]);

    const getTotalLinkClicks = (viewersList) => {
        if (viewersList.length === 0)
            return 0;

        return viewersList.map(employeeDetails => employeeDetails.numberOfClicks).reduce((total, numberOfClicks) => total + numberOfClicks, 0);
    };
    
    const clickCount = viewersList ? getTotalLinkClicks(viewersList) : 0;
    const clicksCountFormatted = `${parseInt(clickCount).toLocaleString("en-us")} ${clickCount > 1 || clickCount === 0 ? "Clicks" : "Click"}`;

    return (
        <StandardModal
            open={showAdClicksModal}
            onClose={handleCloseAdClicksModal}
            title="Ad Clicks"
            widthMode="wide"
            headerAside={(
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect value={userType} onChange={handleUserTypeChange}>
                        <option value={AUDIENCE.EMPLOYEE}>{capitalizeString(pluralize(customNames.employees))}</option>
                        <option value={AUDIENCE.CLIENT}>{capitalizeString(pluralize(customNames.clients))}</option>
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseAdClicksModal}>
                        Close
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionMetricSummary>
                    <UserSelectionMetricCopy>
                        <UserSelectionMetricTitle>View Ad Clicks</UserSelectionMetricTitle>
                        <UserSelectionMetricDescription>
                            Review click data from employees or clients to measure the effectiveness and interaction level of your ad campaign.
                        </UserSelectionMetricDescription>
                    </UserSelectionMetricCopy>
                    {!isFetchingClicks && <UserSelectionMetricValue>{clicksCountFormatted}</UserSelectionMetricValue>}
                </UserSelectionMetricSummary>
            </StandardModalSection>

            <StandardModalSection>
                {isFetchingClicks ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <UserViewerListPreview
                        viewersList={viewersList}
                        isReaderTypeEmployee={userType === AUDIENCE.EMPLOYEE}
                        clientSecondaryHeader="Client Type"
                        emptyMessage="No user clicks found"
                    />
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default AdvertisingClicksModal;
